import { API, deleteRequest, getRequest, postRequest, putRequest } from '@/api/api';
import { UserRole } from '@/utils/roles';

import type {
  CoOwnerInvite,
  CoOwnerInviteDto,
  LeaveOwnershipRequestDto,
  TransferOwnershipRequestDto,
} from './ownership.dto';
import { mapCoOwnerInvite } from './ownership.mappers';

// GET /invites/vessel?role=<role> → pending invitations to the owner group. Gated on
// CrewManagement. Co-owner ones are listed to the owner and co-owners only; the owner one a
// captain sends to an ownerless vessel is listed to the captain too.
export const getOwnershipInvites = async (
  vesselId: string,
  role: UserRole.OWNER | UserRole.CO_OWNER,
): Promise<CoOwnerInvite[]> => {
  const { data } = await getRequest<CoOwnerInviteDto[]>(
    API.PEOPLE,
    '/invites/vessel',
    false,
    { role },
    vesselId,
  );
  // Co-owner codes are listed with the vessel's other codes, beside its crew.
  return (data ?? []).filter(({ type }) => type !== 'code').map(mapCoOwnerInvite);
};

// POST /roles/ownership → 204. Requires TransferVessel and the owner role.
export const transferOwnership = async (vesselId: string, personId: string): Promise<void> => {
  // The TransferVessel check reads the vessel from X-Service-Id, and the vessel page can show a
  // vessel other than the active one.
  await postRequest<void, TransferOwnershipRequestDto>(
    API.PEOPLE,
    '/roles/ownership',
    { service_id: vesselId, person_id: personId },
    false,
    vesselId,
  );
};

// POST /roles/ownership/leave → 204. The owner only.
export const leaveOwnership = async (vesselId: string, successorId?: string): Promise<void> => {
  await postRequest<void, LeaveOwnershipRequestDto>(
    API.PEOPLE,
    '/roles/ownership/leave',
    { service_id: vesselId, person_id: successorId },
    false,
    vesselId,
  );
};

// DELETE /roles → 204. Only the owner removes a co-owner (403 ONLY_OWNER_MANAGES_CO_OWNERS).
// The person keeps their account; a plan they paid for is cancelled at period end.
export const removeCoOwner = async (vesselId: string, personId: string): Promise<void> => {
  await deleteRequest<void, { person_id: string; service_id: string }>(
    API.PEOPLE,
    '/roles',
    { person_id: personId, service_id: vesselId },
    false,
    undefined,
    vesselId,
  );
};

// PUT /roles → 204. Someone already on the vessel becomes a co-owner without an invitation —
// the owner and co-owners may do it, and it needs a free management seat (409 otherwise).
export const promoteToCoOwner = async (vesselId: string, personId: string): Promise<void> => {
  await putRequest<
    void,
    { person_id: string; service_id: string; service_type: string; role: string }
  >(
    API.PEOPLE,
    '/roles',
    { person_id: personId, service_id: vesselId, service_type: 'vessel', role: UserRole.CO_OWNER },
    false,
    vesselId,
  );
};
